// @flow
import React from "react";
import { get, noop } from "lodash";
import type { TopPool } from "@tvg/types/Pool";
import type { UnaryFn } from "@tvg/types/Functional";
import {
  handleTouchMove,
  handleTouchStart,
  evaluateReachingEdge
} from "@tvg/utils/swipeEventHandlers";
import TopPoolMask from "@fdr/static-ui/Masks/topPools";
import { TopPoolsWrapper, Title, TopPoolsContainer } from "./styled-components";
import TopPoolPanel from "../../_molecules/TopPoolPanel";

type Props = {
  /**
   * Title for upper section
   */
  title: string,
  /**
   * List of Top Pools
   */
  pools: TopPool[],
  /**
   * Whenever the component is loading
   */
  isLoading: boolean,
  /**
   * Loading Mask for first request
   */
  hasRequested: boolean,
  /**
   * Event click on pool
   */
  onPoolClick: UnaryFn<TopPool, void>,
  /*
   * Aims to send a gtm event on scrolling
   */
  sendGTMScrollEvent: UnaryFn<boolean, void>,
  /*
   * Show header section
   */
  showHeader?: boolean
};

const handleSwipeGTMEvents = (
  e: SyntheticTouchEvent<*>,
  sendGTMScrollEvent
) => {
  let swipeDirection = handleTouchMove(e);
  const isLivingOnTheEdge = evaluateReachingEdge(e);
  swipeDirection = isLivingOnTheEdge ? 0 : swipeDirection;
  if (swipeDirection !== 0) {
    sendGTMScrollEvent(swipeDirection < 0);
  }
};

const renderTopPools = (
  pools: TopPool[],
  onPoolClick: UnaryFn<TopPool, void>
) =>
  Array.isArray(pools) &&
  pools.length &&
  pools.map((pool) => (
    <TopPoolPanel
      key={`pool-${pool.trackCode}-${get(pool, "race.raceNumber")}-${
        pool.amount
      }`}
      onPanelClick={onPoolClick}
      pool={pool}
    />
  ));

export const TopPools = ({
  title,
  pools,
  isLoading,
  hasRequested,
  onPoolClick,
  sendGTMScrollEvent,
  showHeader = false
}: Props) =>
  isLoading && !hasRequested ? (
    <TopPoolMask />
  ) : (
    pools &&
    pools.length > 0 && (
      <TopPoolsWrapper data-qa-label="topPoolsWrapper" showHeader={showHeader}>
        {showHeader && <Title data-qa-label="topPoolsTitle">{title}</Title>}
        <TopPoolsContainer
          onTouchStart={(e: SyntheticTouchEvent<*>) => handleTouchStart(e)}
          onTouchMove={(e: SyntheticTouchEvent<*>) =>
            handleSwipeGTMEvents(e, sendGTMScrollEvent)
          }
        >
          {renderTopPools(pools.slice(0, 10), onPoolClick)}
        </TopPoolsContainer>
      </TopPoolsWrapper>
    )
  );

TopPools.defaultProps = {
  title: "Top Pools",
  pools: [],
  isLoading: false,
  hasRequested: false,
  onPoolClick: noop,
  sendGTMScrollEvent: noop,
  showHeader: false
};

export default TopPools;
